<template>
    <div class="h-[90dvh] overflow-y-auto p-5 lg:pb-20">
        <div v-if="isRefreshing && wishlistProducts.length === 0" class="flex flex-col gap-6">
            <UiSkeletonWishlistItem v-for="i in 3" :key="`skeleton-${i}`" />
        </div>

        <div v-else class="flex flex-col gap-6">
            <div v-for="wishlistProduct in wishlistProducts" :key="wishlistProduct.itemId" class="w-full">
                <div class="flex items-center gap-6">
                    <div class="h-[125px] w-[100px] shrink-0 bg-white">
                        <NuxtLink
                            :to="'/' + wishlistProduct?.product?.product?.seoUrls[0]?.seoPathInfo"
                            @click="$emit('closeWishlist')">
                            <ShopwareImg
                                class="h-full w-full object-cover"
                                :alt="wishlistProduct.product.product.cover.media.alt"
                                :src="wishlistProduct.product.product.cover.media.url"
                                :width="100" />
                        </NuxtLink>
                    </div>
                    <div class="relative flex w-full items-center gap-2">
                        <div class="relative flex w-full flex-col gap-2">
                            <div class="flex flex-col">
                                <NuxtLink
                                    :to="'/' + wishlistProduct?.product?.product?.seoUrls[0]?.seoPathInfo"
                                    @click="$emit('closeWishlist')"
                                    class="w-fit">
                                    <h3 class="font-mori inline-block text-base font-semibold">
                                        {{ wishlistProduct.product.product.manufacturer.translated.name }}
                                    </h3>
                                </NuxtLink>
                                <NuxtLink
                                    :to="'/' + wishlistProduct?.product?.product?.seoUrls[0]?.seoPathInfo"
                                    @click="$emit('closeWishlist')"
                                    class="w-fit">
                                    <p class="inline-block text-sm leading-none">
                                        {{ wishlistProduct.product.product.translated.name }}
                                    </p>
                                </NuxtLink>
                            </div>

                            <div class="flex w-full flex-wrap items-center justify-start gap-4">
                                <p
                                    class="text-sm font-bold text-black"
                                    :class="{ '!font-medium line-through': isDiscounted(wishlistProduct) }">
                                    <Price
                                        :price="wishlistProduct.product.product.calculatedPrice.listPrice.price"
                                        free-label />
                                </p>
                                <p class="sale-tag-slim" v-if="isDiscounted(wishlistProduct)">
                                    <Price
                                        :price="wishlistProduct.product.product.calculatedPrice.totalPrice"
                                        free-label />
                                </p>
                            </div>
                        </div>

                        <button @click="removeFromWishlistAndRefresh(wishlistProduct.itemId)">
                            <XMarkIcon class="ml-2 size-[22px] stroke-2 duration-200 hover:fill-green-400" />
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="!isRefreshing && wishlistProducts.length === 0">
                <p class="mb-3">{{ $snippets['headless.wishlist.wishlistEmpty'] }}</p>
                <NuxtLink to="/" class="btn btn-primary text-center" @click="$emit('closeWishlist')">
                    {{ $snippets['headless.wishlist.goToProducts'] }}
                </NuxtLink>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { XMarkIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';

    defineEmits<{ closeWishlist: any }>();

    const { search } = useProductSearch();
    const isRefreshing: Ref<Boolean> = ref(false);
    const wishlistProducts: Ref<Array<any>> = ref([]);
    const { items, getWishlistProducts, removeFromWishlist } = useUnifiedWishlist();

    onBeforeMount(async () => {
        await fetchWishlistProducts();
    });

    function isDiscounted(wishlistProduct: any): boolean {
        return (
            wishlistProduct.product.product.calculatedPrice.listPrice.price !==
            wishlistProduct.product.product.calculatedPrice.totalPrice
        );
    }

    async function fetchWishlistProducts() {
        if (isRefreshing.value) {
            return;
        }

        isRefreshing.value = true;

        try {
            await getWishlistProducts();
            const productPromises = items.value.map(async (productId: string) => {
                const productResponse = await search(productId, {
                    criteria: {
                        associations: {
                            mainCategories: {},
                            media: {},
                            manufacturer: {
                                media: {},
                            },
                            categories: {},
                            seoUrls: {},
                        },
                    },
                });
                return { itemId: productId, product: productResponse };
            });

            const results = await Promise.allSettled(productPromises);
            wishlistProducts.value = results
                .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled' && !!r.value)
                .map(r => r.value);
        } finally {
            isRefreshing.value = false;
        }
    }

    async function removeFromWishlistAndRefresh(id: string) {
        await removeFromWishlist(id);
        wishlistProducts.value = wishlistProducts.value.filter(product => product.itemId !== id);
    }
</script>
